// SAB-575: signup password strength as a live 3-segment meter + word label
// (not a rules checklist, per the handoff). Segments fill with --success as
// strength climbs; unmet segments are rgba(255,255,255,.12).
import { cn } from '@/lib/utils';

// 0 = empty, 1 = weak, 2 = good, 3 = strong. Heuristic only — the authoritative
// strength check stays server/validation-side; this is UX feedback.
export function scorePassword(pw: string): 0 | 1 | 2 | 3 {
  if (!pw) return 0;
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  // collapse 0-5 raw into 3 visible tiers
  if (score <= 1) return 1;
  if (score <= 3) return 2;
  return 3;
}

const LABEL: Record<number, string> = { 0: '', 1: 'Weak', 2: 'Good', 3: 'Strong' };
const LABEL_COLOR: Record<number, string> = {
  0: 'text-transparent',
  1: 'text-[hsl(0_84%_62%)]',
  2: 'text-[hsl(var(--success))]',
  3: 'text-[hsl(var(--success))]',
};

export function PasswordStrengthMeter({ password }: { password: string }) {
  const score = scorePassword(password);
  if (!password) return null;
  return (
    <div className="mt-2 flex items-center gap-3">
      <div className="flex flex-1 gap-1.5">
        {[1, 2, 3].map((seg) => (
          <div
            key={seg}
            className={cn(
              'h-1 flex-1 rounded-full transition-colors',
              seg <= score ? 'bg-[hsl(var(--success))]' : 'bg-white/[0.12]',
            )}
          />
        ))}
      </div>
      <span className={cn('text-xs font-semibold w-10 text-right', LABEL_COLOR[score])}>
        {LABEL[score]}
      </span>
    </div>
  );
}

export default PasswordStrengthMeter;
